// when each loop screen airs (Oct 27, programming-plan.md Phase A; every rule approved by Jared, the wording in
// config/clock.json loopRules). A screen without a rule airs every pass. Each rule is `airsNow(now)` on the display;
// snapshot-display.mjs makes a screen whose rule says no read as "no data", so the loop and the scheduler's "up next"
// pass it by. "Every other pass" and "once an hour" are spacings from when the screen last aired (a pass is about ten
// minutes), so they hold up when features cut a pass short.
import { getTimes } from '../../vendor/auto/suncalc.js';
import { data } from './snapshot.mjs';
import crabDays from './crab.mjs';
import marketState from './market-watch.mjs';
import { marketsOn } from './scheduler.mjs';
import { dayKey } from './format.mjs';

const {
	clock, seasons, crabSeason, water,
} = window.RHL;
const rules = clock.loopRules ?? {};
const MINUTE = 60000;
const DAY = 86400000;
const EVERY_OTHER = (rules.everyOtherPassMinutes ?? 15) * MINUTE;
const HOURLY = (rules.hourlyMinutes ?? 55) * MINUTE;

const screen = (id) => window.RHL.screens?.[id];
// aired within the last `ms`
const airedWithin = (display, ms, now) => !!display?.lastAiredAt && now - display.lastAiredAt < ms;

// the day of the week (0 = Sunday) and hour, in the channel's time zone
const local = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return { day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday), hour: Number(parts.hour) };
};

// today's sunrise or sunset at the water point; 20:00 UTC is midday on the Pacific coast
const sunToday = (type, now) => {
	const [y, m, d] = dayKey(now).split('-').map(Number);
	return getTimes(new Date(Date.UTC(y, m - 1, d, 20)), water.point.lat, water.point.lon)[type];
};

// whole days from today to a YYYY-MM-DD date
const daysTo = (date, now) => Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${dayKey(now)}T12:00:00Z`)) / DAY);

const RULES = {
	// every pass in season when a window within three days scores 6 or better; every other pass otherwise (the closed
	// season keeps it in the loop: Jared, no to taking it off)
	'crab-report': function crab(now) {
		const today = dayKey(now);
		const inSeason = !!crabSeason && today >= crabSeason.opener && (!crabSeason.closes || today <= crabSeason.closes);
		const good = inSeason && crabDays({ tides: data('tides'), swell: data('swell'), wind: data('wind') }, now)
			.some((d) => d.score >= (rules.crabGoodScore ?? 6) && new Date(d.high.t) - now < (rules.crabGoodDays ?? 3) * DAY);
		return good || !airedWithin(this, EVERY_OTHER, now);
	},
	// Sunrise Score from midnight until sunrise; Sunset Score from noon until sunset (two screens: the Sky Score merge
	// was a no)
	'sunrise-score': (now) => now < sunToday('sunrise', now),
	'sunset-score': (now) => local(now).hour >= 12 && now < sunToday('sunset', now),
	// off when nothing on the list is within 30 days; every pass the week before one; every other pass between
	'coming-up': function comingUp(now) {
		const days = (seasons.countdowns ?? []).map((c) => daysTo(c.date, now)).filter((d) => d >= 0);
		const soonest = Math.min(Infinity, ...days);
		if (soonest > (rules.comingUpDays ?? 30)) return false;
		return soonest <= (rules.comingUpWeek ?? 7) || !airedWithin(this, EVERY_OTHER, now);
	},
	// the board follows the markets window (off Saturday morning until Asia opens Sunday), and sits out a weekday US
	// session when the Close-Up aired in the last ten minutes
	'market-watch': (now) => {
		if (!marketsOn(now)) return false;
		const { day } = local(now);
		const session = day >= 1 && day <= 5 && marketState(now).stocks !== 'Markets closed';
		return !(session && airedWithin(screen('market-closeup'), 10 * MINUTE, now));
	},
	// every pass from 4 PM and all Friday; once an hour before noon
	'around-town': function aroundTown(now) {
		const { day, hour } = local(now);
		if (day === 5 || hour >= 12) return true;
		return !airedWithin(this, HOURLY, now);
	},
	// Night Sky (Phase E): from just before sunset to sunrise
	'night-sky': (now) => {
		const before = (clock.loopRules?.nightSkyBeforeSunsetMinutes ?? 30) * MINUTE;
		return now >= sunToday('sunset', now) - before || now < sunToday('sunrise', now);
	},
	// once an hour from 1 to 5 AM, unless a stop has a departure within 30 minutes
	'getting-around': function gettingAround(now) {
		const { hour } = local(now);
		if (hour < 1 || hour >= 5) return true;
		const soon = (data('transit') ?? []).some((s) => (s.arrivals ?? []).some((a) => {
			const ms = new Date(a.t) - now;
			return ms >= 0 && ms <= 30 * MINUTE;
		}));
		return soon || !airedWithin(this, HOURLY, now);
	},
};

Object.entries(RULES).forEach(([id, rule]) => {
	const display = screen(id);
	if (display) display.airsNow = rule;
});

// for the console and the control room: which screens the rules are passing by right now
const ruledOut = (now = new Date()) => Object.keys(RULES).filter((id) => screen(id) && !screen(id).airsNow(now));

export default ruledOut;
